// Evidence-based rules engine for insulin and GLP-1 dose suggestions.
// Rules follow ADA 2026, RSSDI India Consensus 2024, KDIGO 2024 and product
// labels. Every suggestion returns rationale + citations so the doctor can
// verify — the algorithm never prescribes silently.
//
// Inputs (all optional; the engine explains what's missing):
//   age (years), sex ('male'|'female'), weight (kg), height (cm),
//   egfr (mL/min/1.73m²), hba1c (%), fbs (mg/dL), ppbs (mg/dL),
//   known: string[] (Known Case Of chips),
//   currentMeds: [{brand_name, generic_name, composition, ...}],
//   pregnant: bool.

function n(v) { const x = parseFloat(v); return Number.isFinite(x) ? x : null; }
function has(known, ...needles) {
  const src = (known || []).join(" ").toLowerCase();
  return needles.some((k) => src.includes(String(k).toLowerCase()));
}
function alreadyOn(currentMeds, ...names) {
  const src = (currentMeds || []).map((m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`).join(" ").toLowerCase();
  return names.some((n) => src.includes(String(n).toLowerCase()));
}
function round1(v) { return Math.round(v * 10) / 10; }
function roundHalf(v) { return Math.round(v * 2) / 2; } // insulin pens dose in 0.5-1 U

// -------- Insulin --------
// Returns { recommend: bool, suggestions: [{regimen, dose, timing, titration, rationale, cautions[], citations[]}], missing: [] }
export function suggestInsulin(ctx) {
  const { age, weight, egfr, hba1c, known = [], currentMeds = [], pregnant = false } = ctx;
  const missing = [];
  if (!weight) missing.push("weight");
  if (hba1c == null) missing.push("HbA1c");
  if (egfr == null) missing.push("eGFR (from Creatinine + age + sex)");

  const isDM = has(known, "diabet", "DM ", " DM", "type 2", "type 1")
    || /diabet|\bgdm\b|gestational/i.test(`${ctx.diagnosisPrimary || ""} ${ctx.diagnosisSecondary || ""}`);
  const already = alreadyOn(currentMeds, "insulin", "glargine", "detemir", "degludec", "lispro", "aspart", "glulisine", "NPH", "70/30", "75/25", "isophane", "biphasic");

  // Bail-out: don't suggest insulin if patient isn't diabetic per known-case chips.
  if (!isDM) {
    return { recommend: false, suggestions: [], missing, reason: "No diabetes tag present in Known Case Of." };
  }

  const suggestions = [];
  const cautions = [];
  const w = weight || 70; // sensible default so we can still compute a range
  const elderly = age != null && age >= 65;
  const frail = age != null && age >= 75;

  // Renal impairment tier — drives all insulin dose reductions.
  let renalTier = "normal";
  if (egfr != null) {
    if (egfr < 30) renalTier = "severe";
    else if (egfr < 45) renalTier = "moderate";
    else if (egfr < 60) renalTier = "mild";
  }
  if (renalTier === "severe") cautions.push("eGFR < 30: reduce total insulin by 25-50% (KDIGO 2024). Watch for hypoglycaemia — insulin clearance is impaired.");
  if (renalTier === "moderate") cautions.push("eGFR 30-44: start at the lower end of the range; titrate more slowly.");
  if (elderly) cautions.push("Age ≥ 65: aim HbA1c 7.0-7.5 %, avoid tight targets. Beers criteria advises caution with sliding scales.");
  if (frail) cautions.push("Age ≥ 75 / frailty: consider once-daily basal-only regimen and HbA1c target 7.5-8.0 %.");
  if (has(known, "hypoglyc")) cautions.push("Recurrent hypoglycaemia: prefer analogue basal (glargine U300 or degludec) — flatter PK.");
  if (has(known, "heart failure")) cautions.push("Heart failure: avoid TZDs; insulin OK — monitor weight & oedema.");
  if (pregnant || isPregnant(ctx)) cautions.push("Pregnancy / GDM: INSULIN is first-line. Only regular insulin, NPH, aspart and detemir are labelled safe (glargine off-label).");

  // Base dose calculations (before adjustments).
  let basalPerKg;
  if (hba1c != null && hba1c > 10) basalPerKg = 0.2;      // higher start
  else basalPerKg = 0.15;                                  // typical start
  if (elderly) basalPerKg = Math.min(basalPerKg, 0.1);     // Beers-safe
  if (renalTier === "severe") basalPerKg *= 0.6;
  else if (renalTier === "moderate") basalPerKg *= 0.75;

  const basalStartDose = roundHalf(basalPerKg * w);
  const basalHighDose = roundHalf(basalPerKg * w * 1.5);

  // Build a ready-to-drop prescription row for each suggestion. The row is
  // structured EXACTLY like /app/frontend/src/pages/PatientDetail.jsx's
  // `emptyRow()` so a single onAddMolecule() call slots it into the meds table.
  const mkRx = (over) => ({
    brand_name: "", generic_name: "", composition: "", dosage_form: "Injection",
    strength: "", route: "SC", frequency: "", duration: "Ongoing — review at follow-up",
    notes: "", quantity: "", instructions: "", type: "insulin",
    ...over,
  });

  // -------------------- Suggestion 1: Basal-only (first line) --------------------
  suggestions.push({
    regimen: "Basal insulin — start",
    drug: "Insulin Glargine U100 (or Detemir / Degludec)",
    dose: `${basalStartDose} U SC once daily at bedtime`,
    titration: "↑ 2 U every 3 days until FBS 80-130 mg/dL. Cap first dose at 10 U if elderly/frail.",
    rationale: `Weight-based start ${basalPerKg.toFixed(2)} U/kg × ${w} kg = ${basalStartDose} U. HbA1c ${hba1c ?? "?"}, eGFR ${egfr ?? "?"} → ${renalTier} renal tier.`,
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9 — Pharmacotherapy for T2DM", "RSSDI 2024 Insulin Initiation"],
    recommended: hba1c != null && hba1c >= 9 && !already,
    rx: mkRx({
      brand_name: "Lantus / Basalog / Toujeo / Tresiba",
      generic_name: "Insulin Glargine (or Detemir / Degludec)",
      strength: `${basalStartDose} U`,
      frequency: "OD (at bedtime)",
      instructions: "Rotate injection sites. Titrate +2 U every 3 days until FBS 80-130.",
    }),
  });

  // -------------------- Suggestion 2: Basal-bolus intensification --------------------
  if (hba1c != null && hba1c >= 10) {
    const tdd = roundHalf(0.4 * w * (renalTier === "severe" ? 0.6 : renalTier === "moderate" ? 0.75 : 1));
    const bas = roundHalf(tdd * 0.5);
    const bolusMeal = roundHalf((tdd * 0.5) / 3);
    suggestions.push({
      regimen: "Basal-bolus (uncontrolled hyperglycaemia)",
      drug: "Glargine (basal) + Lispro/Aspart/Glulisine (prandial)",
      dose: `Basal ${bas} U hs · Prandial ${bolusMeal} U before each of 3 meals (total ${tdd} U/day)`,
      titration: "Adjust basal by fasting SMBG (target 80-130). Adjust prandial by 2-hr postmeal (< 180). Reduce dose 25% for hypoglycaemia < 70 mg/dL.",
      rationale: `HbA1c ${hba1c}% ≥ 10 → TDD 0.4 U/kg (renal-adjusted) = ${tdd} U, split 50:50 basal:bolus.`,
      cautions: cautions.slice(),
      citations: ["ADA 2026 §9.9 — Insulin intensification", "AACE 2024"],
      recommended: hba1c >= 10 && !already,
      rx: mkRx({
        brand_name: "Lantus + Humalog / Novorapid",
        generic_name: "Glargine + Rapid analogue",
        strength: `${bas} U basal hs + ${bolusMeal} U TDS (pre-meal)`,
        frequency: "4 doses/day",
        instructions: `TDD ${tdd} U/day. Titrate basal by FBS, bolus by 2-hr postmeal.`,
      }),
    });
  }

  // -------------------- Suggestion 3: Premixed (fewer injections, elderly-friendly) --------------------
  if (elderly || frail) {
    const tdd = roundHalf(0.3 * w * (renalTier === "severe" ? 0.6 : renalTier === "moderate" ? 0.75 : 1));
    const am = roundHalf(tdd * 2 / 3);
    const pm = roundHalf(tdd - am);
    suggestions.push({
      regimen: "Premixed insulin (BID) — elderly / adherence-friendly",
      drug: "Insulin 70/30 (Mixtard) or 75/25 analogue (Humalog Mix)",
      dose: `${am} U before breakfast + ${pm} U before dinner (total ${tdd} U/day)`,
      titration: "↑ 2 U twice weekly on the dose whose corresponding pre-meal glucose is elevated.",
      rationale: `Elderly/frail with need for insulin — premixed BID has ~30 % fewer injections vs basal-bolus.`,
      cautions: cautions.slice(),
      citations: ["RSSDI 2024 §4 — Premixed insulin in Indian population"],
      recommended: false,
      rx: mkRx({
        brand_name: "Mixtard 70/30 / Humalog Mix 75/25",
        generic_name: "Biphasic Insulin 70/30 or 75/25",
        strength: `${am} U AM + ${pm} U PM`,
        frequency: "BID (pre-breakfast & pre-dinner)",
        instructions: `TDD ${tdd} U/day. Titrate ↑2 U twice weekly.`,
      }),
    });
  }

  // -------------------- Sick-day rule (universal) --------------------
  suggestions.push({
    regimen: "Sick-day rule (patient education)",
    drug: "—",
    dose: "Do NOT stop basal insulin during illness / fasting.",
    titration: "Check SMBG q4h. If > 250 mg/dL or ketones — call clinic. If < 70 — sugar 15 g + recheck 15 min.",
    rationale: "Insulin omission on sick days is the top cause of DKA presentations.",
    cautions: [],
    citations: ["ADA 2026 §16.1"],
    recommended: true,
    // Not a prescribable row — advice only.
    rx: null,
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- GLP-1 receptor agonists --------
// Returns { recommend, suggestions:[{drug, start, titration, target, rationale, cautions, citations, recommended}], missing:[] }
export function suggestGLP1(ctx) {
  const { age, weight, egfr, hba1c, known = [], currentMeds = [], pregnant = false } = ctx;
  const missing = [];
  if (!weight) missing.push("weight");
  if (hba1c == null) missing.push("HbA1c");

  const isDM = has(known, "diabet", "DM ", " DM", "type 2");
  const obese = weight && ctx.height ? (weight / Math.pow(ctx.height / 100, 2)) >= 27 : false;
  const cvd = has(known, "CAD", "post MI", "post PCI", "CABG", "stroke", "TIA", "heart failure");
  const ckd = has(known, "CKD");
  const already = alreadyOn(currentMeds, "semaglutide", "liraglutide", "dulaglutide", "tirzepatide", "exenatide", "lixisenatide");
  const gastroparesis = has(known, "gastroparesis");
  const mtc = has(known, "MTC", "medullary thyroid", "MEN2");
  const pancreatitis = has(known, "pancreatit");

  const cautions = [];
  if (mtc) cautions.push("BLOCKED: personal/family history of medullary thyroid carcinoma or MEN2 — do not prescribe GLP-1 (black-box warning).");
  if (pancreatitis) cautions.push("History of pancreatitis — avoid unless benefit outweighs risk; counsel on abdominal pain.");
  if (gastroparesis) cautions.push("Gastroparesis — avoid (delays gastric emptying further).");
  if (pregnant) cautions.push("Pregnancy — avoid; discontinue ≥ 2 months before planned conception (semaglutide half-life).");
  if (egfr != null && egfr < 15) cautions.push("eGFR < 15: avoid exenatide; semaglutide/dulaglutide/liraglutide are OK but start low and monitor.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: start at half the labelled first dose; titrate slowly for GI tolerability.");

  const suggestions = [];
  const eligibleForCV = cvd || ckd || (weight && weight >= 90);

  // Blocker → return empty
  if (mtc) {
    return { recommend: false, suggestions: [], missing, cautions, reason: "MTC / MEN2 history contraindicates GLP-1." };
  }
  if (isPregnant(ctx)) {
    return { recommend: false, suggestions: [], missing, cautions, reason: "Pregnancy / GDM — GLP-1 contraindicated; insulin is first-line." };
  }
  if (!isDM && !obese) {
    return { recommend: false, suggestions: [], missing, reason: "Not indicated — patient is neither diabetic nor obese (BMI ≥ 27)." };
  }

  const mkGlpRx = (over) => ({
    brand_name: "", generic_name: "", composition: "", dosage_form: "Injection",
    strength: "", route: "SC", frequency: "", duration: "Ongoing — review at follow-up",
    notes: "", quantity: "", instructions: "", type: "injectable",
    ...over,
  });

  // Semaglutide (Ozempic) — first-line weekly, strong CV & renal outcomes.
  suggestions.push({
    drug: "Semaglutide (Ozempic) SC weekly",
    start: "0.25 mg SC once weekly × 4 weeks",
    titration: "→ 0.5 mg weekly × 4 wk → 1.0 mg weekly → up to 2.0 mg weekly for HbA1c/weight goals",
    target: "Maintenance 1.0 mg weekly (max 2.0 mg)",
    rationale: `T2DM ${eligibleForCV ? "with established CV / renal risk" : ""}. Semaglutide reduces MACE by 26 % and slows CKD progression.`,
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9.4 — GLP-1 RA with proven CV benefit", "SUSTAIN-6 (2016)", "FLOW trial (2024)"],
    recommended: !already && !mtc && !gastroparesis,
    rx: mkGlpRx({
      brand_name: "Ozempic / Rybelsus (oral)",
      generic_name: "Semaglutide",
      strength: "0.25 mg (start) → 0.5 mg → 1.0 mg",
      frequency: "Once weekly",
      instructions: "Start 0.25 mg × 4 wk, then 0.5 mg × 4 wk, then 1.0 mg maintenance. Titrate for GI tolerability.",
    }),
  });

  // Dulaglutide — weekly, easy pen, dose 0.75 → 1.5 → 3 → 4.5 mg.
  suggestions.push({
    drug: "Dulaglutide (Trulicity) SC weekly",
    start: "0.75 mg SC once weekly",
    titration: "→ 1.5 mg weekly after 4 wk → 3 mg → 4.5 mg (per response)",
    target: "Maintenance 1.5 mg weekly (max 4.5 mg)",
    rationale: "Once-weekly, established CV benefit (REWIND trial). Simpler pen than semaglutide.",
    cautions: cautions.slice(),
    citations: ["REWIND (2019)", "ADA 2026 §9.4"],
    recommended: !already && !mtc && !gastroparesis && eligibleForCV,
    rx: mkGlpRx({
      brand_name: "Trulicity",
      generic_name: "Dulaglutide",
      strength: "0.75 mg → 1.5 mg",
      frequency: "Once weekly",
      instructions: "Start 0.75 mg × 4 wk → 1.5 mg maintenance. Escalate to 3 / 4.5 mg if HbA1c/weight response inadequate.",
    }),
  });

  // Liraglutide — daily; still used in India for cost & titration control.
  suggestions.push({
    drug: "Liraglutide (Victoza / Saxenda) SC daily",
    start: "0.6 mg SC daily × 1 week",
    titration: "→ 1.2 mg daily → 1.8 mg daily. For weight-loss indication: → 3.0 mg daily.",
    target: "T2DM: 1.2-1.8 mg daily. Obesity: 3.0 mg daily.",
    rationale: obese ? "Obesity indication (BMI ≥ 27 with comorbidity)." : "T2DM with established CV disease (LEADER trial).",
    cautions: cautions.slice(),
    citations: ["LEADER (2016)", "SCALE Obesity (2015)"],
    recommended: !already && !mtc && !gastroparesis && (obese || cvd),
    rx: mkGlpRx({
      brand_name: obese ? "Saxenda" : "Victoza",
      generic_name: "Liraglutide",
      strength: obese ? "0.6 → 1.2 → 1.8 → 2.4 → 3.0 mg" : "0.6 → 1.2 → 1.8 mg",
      frequency: "Once daily",
      instructions: "↑ 0.6 mg weekly to target dose. Take at same time each day. Rotate injection sites.",
    }),
  });

  // Tirzepatide — dual GLP-1/GIP agonist. Best HbA1c + weight outcomes.
  suggestions.push({
    drug: "Tirzepatide (Mounjaro / Zepbound) SC weekly",
    start: "2.5 mg SC once weekly × 4 weeks",
    titration: "→ 5 mg × 4 wk → 7.5 → 10 → 12.5 → 15 mg (per response). Increase in 2.5 mg steps every ≥ 4 weeks.",
    target: "Maintenance 5-15 mg weekly. Most Indians respond well at 5-10 mg.",
    rationale: `Superior HbA1c reduction (~2.3 %) and weight loss (~20 %) vs semaglutide. ${eligibleForCV ? "SURPASS-CVOT showed non-inferiority on MACE." : "Consider when maximum weight-loss and glycaemic effect needed."}`,
    cautions: cautions.slice(),
    citations: ["SURPASS-2 (2021)", "SURMOUNT-1 (2022)", "ADA 2026 §9.4"],
    recommended: !already && !mtc && !gastroparesis && (hba1c != null && hba1c >= 9),
    rx: mkGlpRx({
      brand_name: "Mounjaro / Zepbound",
      generic_name: "Tirzepatide",
      strength: "2.5 mg → 5 mg (titrate)",
      frequency: "Once weekly",
      instructions: "Start 2.5 mg × 4 wk → 5 mg maintenance. Escalate in 2.5 mg steps every ≥4 wk up to 15 mg for target HbA1c / weight loss.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// ============================================================================
// Preventive / Chronic-care push algorithms (oral)
// ============================================================================
// Every function below returns the SAME shape as the injectable engines:
//   { recommend: bool, suggestions: [{ regimen, drug, dose, titration,
//     rationale, cautions[], citations[], recommended, rx }], missing: [],
//     reason?: string }
// The `rx` object is the ready-to-drop prescription row (matches emptyRow() in
// PatientDetail.jsx). If rx is null the card is advisory only.
//
// Common helpers:
function mkOralRx(over) {
  return {
    brand_name: "", generic_name: "", composition: "", dosage_form: "Tablet",
    strength: "", route: "Oral", frequency: "", duration: "Ongoing — review at follow-up",
    notes: "", quantity: "", instructions: "", type: "chronic",
    ...over,
  };
}

// Pull ASCVD / CV disease burden from Known Case Of + primary/secondary Dx text.
function ctxAscvd(known, dx = "") {
  const src = ((known || []).join(" ") + " " + (dx || "")).toLowerCase();
  return /(cad|coronary|angina|post ?mi|myocardial infarction|post ?pci|cabg|stent|stroke|tia|peripheral arter|pad|atherosc|ischaemic|ischemic)/i.test(src);
}
function ctxHeartFailure(known, dx = "") {
  const src = ((known || []).join(" ") + " " + (dx || "")).toLowerCase();
  return /(heart failure|hfref|hfpef|hfmref|cardiomyopathy)/i.test(src);
}
function ctxCkdStage(known, dx = "", egfr = null) {
  const src = ((known || []).join(" ") + " " + (dx || "")).toLowerCase();
  const tag = /ckd stage ([1-5][ab]?)/i.exec(src);
  if (tag) return tag[1].toLowerCase();
  if (egfr == null) return null;
  if (egfr >= 90) return "1";
  if (egfr >= 60) return "2";
  if (egfr >= 45) return "3a";
  if (egfr >= 30) return "3b";
  if (egfr >= 15) return "4";
  return "5";
}
function ctxDiabetes(known, dx = "") {
  const src = ((known || []).join(" ") + " " + (dx || "")).toLowerCase();
  const t1 = /(type ?1|t1dm|iddm)/i.test(src);
  const t2 = /(type ?2|t2dm|diabet|dm\b|niddm)/i.test(src);
  return { t1, t2: t2 && !t1 };
}
function ctxHtn(known, dx = "", sbp, dbp) {
  const src = ((known || []).join(" ") + " " + (dx || "")).toLowerCase();
  const chip = /(hypertens|htn)/i.test(src);
  const bp = (sbp && sbp >= 140) || (dbp && dbp >= 90);
  return chip || bp;
}
function isPregnant(ctx) {
  // Pregnancy must be detected from EVERY signal the doctor may have used:
  // the explicit flag, allergy/context flags, Known-case chips ("Gestational
  // DM", "Pregnancy") and the typed diagnosis ("Gestational Diabetes", "GDM").
  const src = `${(ctx.known || []).join(" ")} ${ctx.diagnosisPrimary || ""} ${ctx.diagnosisSecondary || ""}`.toLowerCase();
  return !!ctx.pregnant ||
    /pregnan|gestational|\bgdm\b|antenatal/.test(src) ||
    (ctx.allergyFlags || []).some((f) => /pregnan/i.test(f));
}

// -------- SGLT2 inhibitors (Empagliflozin / Dapagliflozin) --------
// Indications (per KDIGO 2024, ESC 2023, ADA 2026 §11):
//   • T2DM + eGFR ≥ 20 (renal + CV outcomes)
//   • CKD (with or without DM) with UACR ≥ 200 mg/g and eGFR ≥ 20
//   • HFrEF (LVEF ≤ 40) OR HFpEF — any eGFR ≥ 20
// Blockers: T1DM (DKA risk), eGFR < 20, pregnancy, recurrent GU infection,
// active DKA history, symptomatic hypovolaemia.
export function suggestSGLT2(ctx) {
  const { egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "" } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const missing = [];
  if (egfr == null) missing.push("eGFR");

  const { t1, t2 } = ctxDiabetes(known, dx);
  const hf = ctxHeartFailure(known, dx);
  const uacr = n(ctx.labs?.acr) || n(ctx.labs?.uacr);
  const uAlb = n(ctx.labs?.urine_alb);
  const albuminuric = (uacr != null && uacr >= 30) || (uAlb != null && uAlb >= 30) || has(known, "proteinuria", "albuminuria");
  const ckd = has(known, "CKD") || (egfr != null && egfr < 60);
  const eligible = (t2 && (egfr == null || egfr >= 20)) || hf || (ckd && albuminuric && (egfr == null || egfr >= 20));

  const already = alreadyOn(currentMeds, "empagliflozin", "dapagliflozin", "canagliflozin", "ertugliflozin", "sglt2");
  const cautions = [];
  if (t1) cautions.push("BLOCKED: Type 1 DM — SGLT2 raises DKA risk without proportional benefit (do not push).");
  if (egfr != null && egfr < 20) cautions.push("BLOCKED: eGFR < 20 — SGLT2 initiation not indicated.");
  if (isPregnant(ctx)) cautions.push("BLOCKED: Pregnancy — SGLT2 contraindicated.");
  if (has(known, "recurrent uti", "recurrent genital", "candidiasis")) cautions.push("Recurrent GU infections — counsel on genital hygiene; consider alternative.");
  if (egfr != null && egfr >= 20 && egfr < 30) cautions.push("eGFR 20-30: modest glycaemic effect but retained CV/renal benefit — continue full dose.");
  if (has(known, "type 1")) cautions.push("Do not use in T1DM (DKA risk).");

  const blocked = t1 || (egfr != null && egfr < 20) || isPregnant(ctx);
  if (!eligible) {
    return { recommend: false, suggestions: [], missing, reason: "No T2DM / HFrEF / albuminuric CKD trigger detected." };
  }
  if (blocked) {
    return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible on paper but a hard contraindication blocks initiation — see cautions." };
  }

  const suggestions = [];
  const trigger = t2 && hf ? "T2DM + HF" : t2 && albuminuric ? "T2DM + albuminuric CKD" : hf ? "Heart Failure" : albuminuric ? "Albuminuric CKD (KDIGO)" : "T2DM";
  const rationale = `${trigger}. eGFR ${egfr ?? "?"}${uacr ? `, UACR ${uacr} mg/g` : ""}. SGLT2i reduce MACE, HF hospitalisation and CKD progression (~30-40 %) independent of glycaemia.`;

  // Empagliflozin — first-line (EMPEROR + EMPA-KIDNEY + EMPA-REG).
  suggestions.push({
    regimen: "Empagliflozin — first-line SGLT2i",
    drug: "Empagliflozin",
    dose: "10 mg PO once daily (may increase to 25 mg for glycaemic control)",
    titration: "Start 10 mg OD. Recheck eGFR & K+ in 2-4 weeks (expect a small ~5 mL/min dip — acceptable). Increase to 25 mg for HbA1c if tolerating.",
    rationale,
    cautions: cautions.slice(),
    citations: ["KDIGO 2024 §3.2 — SGLT2i in DKD", "ESC 2023 — HFrEF & HFpEF", "EMPA-REG OUTCOME", "EMPEROR-Reduced/Preserved", "EMPA-KIDNEY"],
    recommended: !already && !blocked,
    rx: mkOralRx({
      brand_name: "Jardiance / Emburu / Glyxamby",
      generic_name: "Empagliflozin",
      composition: "Empagliflozin",
      strength: "10 mg",
      frequency: "OD (morning)",
      instructions: "Take with or without food. Hold during illness/dehydration (sick-day rule). Watch for genital mycotic infection.",
    }),
  });

  // Dapagliflozin — DAPA-HF, DAPA-CKD.
  suggestions.push({
    regimen: "Dapagliflozin — alternative SGLT2i",
    drug: "Dapagliflozin",
    dose: "10 mg PO once daily",
    titration: "Fixed dose — no titration required. Reassess renal + volume status at 2-4 weeks.",
    rationale: `${trigger}. DAPA-HF cut HF hospitalisation by 26 %; DAPA-CKD slowed eGFR decline in CKD ± DM.`,
    cautions: cautions.slice(),
    citations: ["DAPA-HF (2019)", "DAPA-CKD (2020)", "ADA 2026 §11.4"],
    recommended: false, // recommend Empa first; keep this as alternative
    rx: mkOralRx({
      brand_name: "Forxiga / Dapamet / Dapaford",
      generic_name: "Dapagliflozin",
      composition: "Dapagliflozin",
      strength: "10 mg",
      frequency: "OD (morning)",
      instructions: "Hold during illness/dehydration. Counsel on GU hygiene. Reassess eGFR in 2-4 weeks.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- High-intensity Statin (Rosuvastatin / Atorvastatin) --------
// Intensity choice per AHA/ACC 2018 & 2023 (ACC/AHA Chronic Coronary Disease):
//   • High-intensity: prior ASCVD, LDL ≥ 190, DM age 40-75 with 10-yr ASCVD ≥ 20 %.
//   • Moderate-intensity: DM age 40-75 with LDL 70-189 & 10-yr risk 7.5-20 %.
// India-specific: Rosuvastatin 20 mg has ~50 % LDL reduction (similar to Atorva 80 mg)
// and is generally better tolerated in South-Asians (STELLAR).
export function suggestStatin(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const ldl = n(labs.ldl);
  const tc = n(labs.tc);
  const nonHdl = n(labs.non_hdl);
  const alt = n(labs.alt);
  const ast = n(labs.ast);
  const missing = [];
  if (ldl == null) missing.push("LDL");

  const ascvd = ctxAscvd(known, dx);
  const { t1, t2 } = ctxDiabetes(known, dx);
  const dm = t1 || t2;
  const already = alreadyOn(currentMeds, "atorvastatin", "rosuvastatin", "simvastatin", "pravastatin", "pitavastatin", "lovastatin");

  const cautions = [];
  if (isPregnant(ctx)) cautions.push("BLOCKED: Pregnancy / planning conception — statins contraindicated.");
  if (alt != null && alt > 3 * 40) cautions.push("BLOCKED: ALT > 3× ULN — investigate & correct hepatic cause first.");
  if (has(known, "rhabdomyolysis", "myopathy", "statin intolerance")) cautions.push("History of statin-induced myopathy — start Rosuva 5 mg + CK monitoring OR try alternate-day dosing.");
  if (age != null && age >= 75 && !ascvd) cautions.push("Age ≥ 75 without established ASCVD — shared decision; benefit modest, still reasonable if life-expectancy > 5 yr.");
  if (has(known, "cirrhosis", "decompensated liver")) cautions.push("Decompensated liver disease — avoid.");

  const blocked = isPregnant(ctx) || (alt != null && alt > 3 * 40);

  // Decide intensity
  let intensity = null; // 'high' | 'moderate' | null
  let trigger = "";
  if (ascvd) { intensity = "high"; trigger = "Established ASCVD (secondary prevention)"; }
  else if (ldl != null && ldl >= 190) { intensity = "high"; trigger = "LDL ≥ 190 mg/dL (severe primary hypercholesterolaemia)"; }
  else if (dm && age != null && age >= 40 && age <= 75) { intensity = "high"; trigger = "Diabetes, age 40-75 (ADA + AHA/ACC primary prevention)"; }
  else if (age != null && age >= 40 && ldl != null && ldl >= 100 && (has(known, "hypertens") || has(known, "smok"))) { intensity = "moderate"; trigger = "Multiple risk factors (HTN / smoking) with LDL ≥ 100"; }

  if (!intensity) {
    return { recommend: false, suggestions: [], missing, reason: "No high-risk trigger (ASCVD, LDL ≥ 190, DM age 40-75, or multi-RF) detected." };
  }
  if (blocked) {
    return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication blocks initiation — see cautions." };
  }

  const suggestions = [];
  const ldlTarget = ascvd ? "< 55 mg/dL (ESC 2019 very high-risk) or ≥ 50 % reduction" : dm ? "< 70 mg/dL" : "< 100 mg/dL";
  const rationale = `${trigger}. LDL ${ldl ?? "?"} mg/dL${nonHdl ? `, non-HDL ${nonHdl}` : ""}${tc ? `, TC ${tc}` : ""}. Goal: ${ldlTarget}.`;

  if (intensity === "high") {
    // Rosuvastatin high-intensity 20 mg first-line for South-Asians.
    suggestions.push({
      regimen: "Rosuvastatin — high-intensity (first-line SA)",
      drug: "Rosuvastatin",
      dose: "20 mg PO once daily HS (increase to 40 mg if LDL target not met in 6-8 wk)",
      titration: "Recheck lipid + LFT + CK at 6-8 weeks. If LDL not at target → up-titrate 20 → 40 mg. If muscle pain → check CK, consider Rosuva 5-10 mg + Ezetimibe 10 mg.",
      rationale,
      cautions: cautions.slice(),
      citations: ["AHA/ACC 2023 Chronic Coronary Disease", "AACE 2024 Dyslipidaemia", "STELLAR (2003) — South-Asian data", "ADA 2026 §10.4"],
      recommended: !already && !blocked,
      rx: mkOralRx({
        brand_name: "Rosuvas / Crestor / Rozavel",
        generic_name: "Rosuvastatin",
        composition: "Rosuvastatin",
        strength: "20 mg",
        frequency: "OD (bedtime)",
        instructions: `Target LDL ${ldlTarget}. Recheck lipids + LFT at 6-8 wk. Report muscle pain (check CK).`,
      }),
    });
    // Atorvastatin high-intensity — alternative.
    suggestions.push({
      regimen: "Atorvastatin — high-intensity (alternative)",
      drug: "Atorvastatin",
      dose: "40 mg PO once daily (increase to 80 mg if LDL not at target)",
      titration: "Similar to Rosuva. Preferred in CKD (no dose reduction needed).",
      rationale: "Alternative high-intensity option; useful when Rosuvastatin unavailable or in stage 4-5 CKD.",
      cautions: cautions.slice(),
      citations: ["AHA/ACC 2023", "TNT trial (2005)"],
      recommended: false,
      rx: mkOralRx({
        brand_name: "Atorva / Lipitor / Storvas",
        generic_name: "Atorvastatin",
        composition: "Atorvastatin",
        strength: "40 mg",
        frequency: "OD (bedtime)",
        instructions: `Target LDL ${ldlTarget}. Recheck lipids + LFT at 6-8 wk. No dose cap in CKD.`,
      }),
    });
  } else {
    // Moderate-intensity — Atorva 10-20 mg or Rosuva 5-10 mg
    suggestions.push({
      regimen: "Atorvastatin — moderate-intensity",
      drug: "Atorvastatin",
      dose: "20 mg PO once daily HS",
      titration: "Recheck lipids at 6-8 weeks; up-titrate to 40 mg if LDL > target.",
      rationale,
      cautions: cautions.slice(),
      citations: ["AHA/ACC 2023 primary prevention", "AACE 2024"],
      recommended: !already && !blocked,
      rx: mkOralRx({
        brand_name: "Atorva / Storvas / Lipvas",
        generic_name: "Atorvastatin",
        composition: "Atorvastatin",
        strength: "20 mg",
        frequency: "OD (bedtime)",
        instructions: `Target LDL ${ldlTarget}. Recheck lipids + LFT at 6-8 wk.`,
      }),
    });
  }

  return { recommend: true, suggestions, missing, cautions };
}

// -------- ACE-inhibitor / ARB (Ramipril / Telmisartan / Losartan) --------
// Indications (KDIGO 2024, ADA 2026 §11, ESC 2023, JNC-8):
//   • HTN (SBP ≥ 140 or DBP ≥ 90) — first-line especially in DM
//   • DM + albuminuria (UACR ≥ 30 mg/g) — nephroprotection irrespective of BP
//   • HFrEF (four-pillar therapy)
//   • CKD with proteinuria (KDIGO Grade 1A)
//   • Post-MI with LV dysfunction
// Prefer ARB (Telmisartan / Losartan) over ACEi if cough. Do NOT combine ACEi + ARB.
export function suggestACEiARB(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const sbp = n(ctx.vitals?.sbp) || n(ctx.sbp);
  const dbp = n(ctx.vitals?.dbp) || n(ctx.dbp);
  const egfr = n(ctx.egfr);
  const k = n(labs.potassium) || n(labs.k);
  const uacr = n(labs.acr) || n(labs.uacr);
  const uAlb = n(labs.urine_alb);
  const missing = [];
  if (sbp == null && dbp == null) missing.push("BP");
  if (k == null) missing.push("Serum potassium");

  const { t1, t2 } = ctxDiabetes(known, dx);
  const dm = t1 || t2;
  const htn = ctxHtn(known, dx, sbp, dbp);
  const hf = ctxHeartFailure(known, dx);
  const albuminuric = (uacr != null && uacr >= 30) || (uAlb != null && uAlb >= 30) || has(known, "proteinuria", "albuminuria");
  const postMI = has(known, "post MI", "myocardial infarction", "post-MI");
  const ckd = has(known, "CKD") || (egfr != null && egfr < 60);

  const eligible = htn || (dm && albuminuric) || hf || postMI || (ckd && albuminuric);
  if (!eligible) {
    return { recommend: false, suggestions: [], missing, reason: "No HTN / albuminuria / HF / post-MI trigger detected." };
  }

  const already = alreadyOn(currentMeds, "ramipril", "enalapril", "lisinopril", "perindopril", "captopril", "telmisartan", "losartan", "olmesartan", "valsartan", "irbesartan", "candesartan");
  const onACEi = alreadyOn(currentMeds, "ramipril", "enalapril", "lisinopril", "perindopril", "captopril");
  const onARB = alreadyOn(currentMeds, "telmisartan", "losartan", "olmesartan", "valsartan", "irbesartan", "candesartan");
  const cough = has(known, "ace cough", "ACEi cough");

  const cautions = [];
  if (isPregnant(ctx)) cautions.push("BLOCKED: Pregnancy — ACEi/ARB teratogenic; use methyldopa/labetalol instead.");
  if (k != null && k > 5.5) cautions.push("BLOCKED: Hyperkalaemia (K > 5.5) — correct first, or start at lower dose with close K+ monitoring.");
  if (egfr != null && egfr < 30) cautions.push("eGFR < 30: initiate with caution — expect ~10-25 % creatinine bump; unacceptable if > 30 %. Recheck in 1-2 wk.");
  if (has(known, "renal artery stenosis", "bilateral RAS")) cautions.push("BLOCKED: bilateral renal artery stenosis — avoid.");
  if (has(known, "angioedema")) cautions.push("BLOCKED: history of ACEi/ARB angioedema — try alternative class.");
  if (onACEi && onARB) cautions.push("Patient already on ACEi + ARB — dual RAASi is contraindicated (ONTARGET). Deprescribe one.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: start at half the labelled dose; monitor postural BP.");

  const blocked = isPregnant(ctx) || (k != null && k > 5.5) || has(known, "renal artery stenosis", "bilateral RAS", "angioedema");
  if (blocked) {
    return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication — see cautions." };
  }

  const suggestions = [];
  const trigger = hf ? "HFrEF (four-pillar therapy)"
    : (dm && albuminuric) ? "DM + albuminuria (nephroprotection, KDIGO)"
    : (ckd && albuminuric) ? "Albuminuric CKD (nephroprotection)"
    : postMI ? "Post-MI LV dysfunction"
    : "Hypertension";
  const rationale = `${trigger}. BP ${sbp ?? "?"}/${dbp ?? "?"} mmHg, eGFR ${egfr ?? "?"}, K ${k ?? "?"}${uacr ? `, UACR ${uacr}` : ""}. RAAS blockade slows CKD progression, cuts HF admissions & post-MI mortality.`;

  // Telmisartan — first-line ARB (once-daily, long t½, well tolerated).
  suggestions.push({
    regimen: cough ? "Telmisartan — switch from ACEi (cough)" : "Telmisartan — first-line ARB",
    drug: "Telmisartan",
    dose: "40 mg PO once daily (up-titrate to 80 mg after 2-4 wk if BP not at goal)",
    titration: "Recheck BP, creatinine & K+ in 1-2 weeks. Up-titrate 40 → 80 mg for BP < 130/80.",
    rationale,
    cautions: cautions.slice(),
    citations: ["KDIGO 2024 §4 — RAASi in CKD", "ONTARGET (2008)", "ADA 2026 §11.3", "AHA/ACC HTN 2017"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Telma / Micardis / Telsartan",
      generic_name: "Telmisartan",
      composition: "Telmisartan",
      strength: "40 mg",
      frequency: "OD (morning)",
      instructions: "Target BP < 130/80. Recheck creatinine + K+ in 1-2 wk. Report dizziness / dry cough (unusual with ARB).",
    }),
  });

  // Ramipril — ACEi (cheaper, HOPE trial data).
  suggestions.push({
    regimen: "Ramipril — ACEi alternative",
    drug: "Ramipril",
    dose: "2.5 mg PO once daily → titrate to 5-10 mg OD",
    titration: "Start 2.5 mg OD, double every 2-4 wk to target 10 mg (or max tolerated). Recheck creatinine + K+ each step.",
    rationale: "ACEi with strong CV mortality benefit (HOPE, EUROPA). Choose over ARB when cost is dominant factor and no cough history.",
    cautions: cautions.slice(),
    citations: ["HOPE (2000)", "EUROPA (2003)", "ESC 2023 HFrEF"],
    recommended: false, // recommend ARB first unless cost
    rx: mkOralRx({
      brand_name: "Cardace / Ramipres / Zorem",
      generic_name: "Ramipril",
      composition: "Ramipril",
      strength: "2.5 mg",
      frequency: "OD (morning)",
      instructions: "Titrate to 5-10 mg. Report dry cough (switch to ARB if persistent). Recheck creatinine + K+ 1-2 wk.",
    }),
  });

  // Losartan — cheapest ARB in India.
  suggestions.push({
    regimen: "Losartan — economical ARB",
    drug: "Losartan",
    dose: "50 mg PO once daily → 100 mg if BP not at goal",
    titration: "Start 50 mg OD, up-titrate to 100 mg after 3-6 wk. Also lowers uric acid (useful in gout).",
    rationale: "Cheapest ARB in India; LIFE + RENAAL trial data. Preferred if patient has coexisting hyperuricaemia.",
    cautions: cautions.slice(),
    citations: ["RENAAL (2001)", "LIFE (2002)"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Losar / Repace / Losacar",
      generic_name: "Losartan",
      composition: "Losartan",
      strength: "50 mg",
      frequency: "OD (morning)",
      instructions: "Titrate to 100 mg. Also lowers urate — useful in gout.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Metformin (T2DM first-line) --------
// eGFR gating (ADA 2026 §9.1 & KDIGO 2024):
//   ≥ 45      → full dose (500 mg BD titrate to 1 g BD)
//   30-44     → halve dose, monitor eGFR every 3 mo
//   < 30      → discontinue / do not initiate
// Blockers: T1DM, active liver disease, alcohol dependence, IV contrast within 48 h,
// severe hypoxia / acute illness (lactic acidosis risk).
export function suggestMetformin(ctx) {
  const { age, egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const alt = n(labs.alt);
  const hba1c = n(labs.hba1c);
  const missing = [];
  if (egfr == null) missing.push("eGFR");

  const { t1, t2 } = ctxDiabetes(known, dx);
  const preDm = has(known, "prediabet", "IGT", "IFG") || (hba1c != null && hba1c >= 5.7 && hba1c < 6.5);
  const pcos = has(known, "PCOS", "polycystic");
  const eligible = t2 || preDm || pcos;
  if (!eligible) {
    return { recommend: false, suggestions: [], missing, reason: "Not indicated — no T2DM / pre-diabetes / PCOS trigger." };
  }
  if (t1) {
    return { recommend: false, suggestions: [], missing, reason: "Type 1 DM — insulin is first-line; metformin add-on only in select obese T1DM." };
  }
  if (isPregnant(ctx)) {
    return {
      recommend: false, suggestions: [], missing,
      cautions: ["BLOCKED: Pregnancy / Gestational DM — INSULIN is first-line (ADA 2026 §15, RSSDI GDM). Metformin in GDM only under specialist obstetric care."],
      reason: "Pregnancy / GDM — oral agents not recommended; insulin is first-line.",
    };
  }

  const already = alreadyOn(currentMeds, "metformin", "glucophage", "obimet", "gluconorm");
  const cautions = [];
  if (egfr != null && egfr < 30) cautions.push("BLOCKED: eGFR < 30 — do not initiate; stop if already on.");
  if (has(known, "cirrhosis", "decompensated liver", "alcoholic liver")) cautions.push("BLOCKED: decompensated liver / alcohol dependence — lactic-acidosis risk.");
  if (alt != null && alt > 3 * 40) cautions.push("ALT > 3× ULN — investigate before start.");
  if (age != null && age >= 80) cautions.push("Age ≥ 80: verify eGFR, avoid if renal function unstable (Beers criteria).");
  if (has(known, "recent iv contrast")) cautions.push("Hold metformin 48 h after IV contrast; resume if eGFR stable.");

  const blocked = (egfr != null && egfr < 30) || has(known, "cirrhosis", "decompensated liver", "alcoholic liver");
  if (blocked) {
    return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication — see cautions." };
  }

  const suggestions = [];
  let strength, freq, maxDaily, gate, instrExtra;
  if (egfr == null || egfr >= 45) {
    strength = "500 mg"; freq = "BD"; maxDaily = "2000 mg/day";
    gate = "eGFR ≥ 45 — full dose"; instrExtra = "Start 500 mg BD; titrate to 1 g BD over 2-4 weeks.";
  } else {
    strength = "500 mg"; freq = "OD"; maxDaily = "1000 mg/day";
    gate = "eGFR 30-44 — halve dose, monitor 3-monthly"; instrExtra = "Start 500 mg OD; max 500 mg BD. Recheck eGFR q3 months.";
  }
  const rationale = `${t2 ? "T2DM" : preDm ? "Pre-diabetes" : "PCOS"} — Metformin is first-line (ADA 2026 §9.1). ${gate}. HbA1c ${hba1c ?? "?"} %.`;

  suggestions.push({
    regimen: "Metformin — T2DM first-line",
    drug: "Metformin (immediate release)",
    dose: `${strength} PO ${freq} — max ${maxDaily}`,
    titration: `${instrExtra} Take with meals to reduce GI upset. Switch to SR if diarrhoea persists.`,
    rationale,
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9.1", "RSSDI 2024 India Consensus", "KDIGO 2024 §3.1", "UKPDS 34 (1998)"],
    recommended: !already && !blocked,
    rx: mkOralRx({
      brand_name: "Glycomet / Obimet / Gluconorm",
      generic_name: "Metformin",
      composition: "Metformin",
      strength,
      frequency: freq,
      instructions: `${instrExtra} Take with meals. ${gate}. Recheck eGFR + B12 annually.`,
    }),
  });

  // Metformin SR fallback for GI intolerance.
  suggestions.push({
    regimen: "Metformin SR — for GI intolerance",
    drug: "Metformin Sustained-Release",
    dose: "500 mg PO OD with dinner → titrate to 1000-2000 mg OD",
    titration: "One tablet per day, ↑ 500 mg every 1-2 wk. Better tolerated than IR.",
    rationale: "Switch to SR if IR causes diarrhoea/bloating. Once-daily improves adherence.",
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9.1", "Meta-analysis 2016 (metformin XR tolerability)"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Glycomet SR / Obimet SR / Glyciphage SR",
      generic_name: "Metformin SR",
      composition: "Metformin (sustained release)",
      strength: "500 mg SR",
      frequency: "OD (with dinner)",
      instructions: "Swallow whole. Titrate weekly. Recheck eGFR + B12 annually.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Aspirin 75 mg (Secondary Prevention only) --------
// AHA/ACC 2023 + ADA 2026 §10.5: aspirin ONLY for secondary prevention of ASCVD.
// Primary prevention: individualise; NOT routine (bleeding risk outweighs benefit
// per ASCEND, ARRIVE, ASPREE).
export function suggestAspirin(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "" } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const missing = [];

  const ascvd = ctxAscvd(known, dx);
  const bleeding = has(known, "active bleeding", "GI bleed", "peptic ulcer bleed", "haemorrhagic stroke", "hemorrhagic stroke", "thrombocytopenia", "aspirin allergy");
  const already = alreadyOn(currentMeds, "aspirin", "acetylsalicylic", "ecosprin", "loprin");
  const onDoac = alreadyOn(currentMeds, "warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban");

  const cautions = [];
  if (bleeding) cautions.push("BLOCKED: bleeding risk / aspirin allergy — do not initiate.");
  if (onDoac) cautions.push("Patient on anticoagulant — combining with aspirin roughly triples major bleeding. Only continue if strong indication (e.g. recent PCI); add PPI.");
  if (age != null && age >= 70 && !ascvd) cautions.push("Age ≥ 70 without established ASCVD — ASPREE showed net harm. Do not initiate for primary prevention.");
  if (has(known, "peptic ulcer", "gastritis")) cautions.push("Peptic ulcer / gastritis — add PPI (pantoprazole 40 mg OD) if aspirin essential.");

  if (!ascvd) {
    return { recommend: false, suggestions: [], missing, reason: "No established ASCVD — aspirin not recommended for primary prevention." };
  }
  if (bleeding) {
    return { recommend: true, suggestions: [], missing, cautions, reason: "Established ASCVD but active bleeding / allergy blocks aspirin." };
  }

  const suggestions = [];
  suggestions.push({
    regimen: "Aspirin — secondary prevention",
    drug: "Aspirin (low-dose)",
    dose: "75 mg PO once daily (after food)",
    titration: "No titration. Continue lifelong unless bleeding.",
    rationale: `Established ASCVD (${dx || "per Known Case Of"}) — Antithrombotic Trialists' meta-analysis: 22 % reduction in serious vascular events.`,
    cautions: cautions.slice(),
    citations: ["AHA/ACC 2023 Chronic Coronary Disease", "ADA 2026 §10.5 — Antiplatelet Agents", "ATC meta-analysis (2009)"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Ecosprin / Loprin / Aspirin",
      generic_name: "Aspirin",
      composition: "Aspirin (Acetylsalicylic acid)",
      strength: "75 mg",
      frequency: "OD (after food)",
      instructions: "Lifelong for secondary prevention. Stop for surgery per surgeon's advice. Add PPI if PUD history.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Finerenone (non-steroidal MRA for DKD) --------
// FIDELIO-DKD + FIGARO-DKD (2020-21), KDIGO 2024:
//   T2DM + UACR ≥ 30 + eGFR ≥ 25 + K ≤ 4.8 → add finerenone on top of ACEi/ARB + SGLT2i.
export function suggestFinerenone(ctx) {
  const { egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const uacr = n(labs.acr) || n(labs.uacr);
  const uAlb = n(labs.urine_alb);
  const k = n(labs.potassium) || n(labs.k);
  const missing = [];
  if (egfr == null) missing.push("eGFR");
  if (k == null) missing.push("Serum potassium");
  if (uacr == null && uAlb == null) missing.push("UACR");

  const { t2 } = ctxDiabetes(known, dx);
  const albuminuric = (uacr != null && uacr >= 30) || (uAlb != null && uAlb >= 30) || has(known, "proteinuria", "albuminuria");
  const eligible = t2 && albuminuric && egfr != null && egfr >= 25;
  if (!eligible) {
    return { recommend: false, suggestions: [], missing, reason: "Requires T2DM + UACR ≥ 30 + eGFR ≥ 25." };
  }

  const already = alreadyOn(currentMeds, "finerenone", "kerendia");
  const cautions = [];
  if (k != null && k > 4.8) cautions.push("BLOCKED: K > 4.8 — correct first (dietary counselling, loop diuretic) before starting finerenone.");
  if (isPregnant(ctx)) cautions.push("BLOCKED: pregnancy — contraindicated.");
  if (has(known, "addison", "adrenal insufficiency")) cautions.push("BLOCKED: adrenal insufficiency.");
  if (!alreadyOn(currentMeds, "ramipril", "telmisartan", "losartan", "enalapril", "perindopril", "olmesartan", "valsartan")) cautions.push("Preferably initiate AFTER ACEi/ARB is optimised — synergistic RAAS blockade needs baseline RAASi.");

  const blocked = (k != null && k > 4.8) || isPregnant(ctx) || has(known, "addison");
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication — see cautions." };

  const startDose = egfr >= 60 ? "20 mg" : "10 mg";
  const suggestions = [{
    regimen: "Finerenone — non-steroidal MRA (DKD add-on)",
    drug: "Finerenone",
    dose: `${startDose} PO once daily`,
    titration: "Recheck K+ in 4 weeks. If K < 4.8 and eGFR stable → up-titrate to 20 mg OD (target). If K 4.8-5.5 → hold; if K > 5.5 → stop.",
    rationale: `T2DM + albuminuric CKD (UACR ${uacr ?? uAlb} mg/g, eGFR ${egfr}). FIDELIO-DKD showed 18 % reduction in kidney failure/CV death on top of ACEi/ARB.`,
    cautions: cautions.slice(),
    citations: ["KDIGO 2024 §4.3", "FIDELIO-DKD (2020)", "FIGARO-DKD (2021)"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Kerendia",
      generic_name: "Finerenone",
      composition: "Finerenone",
      strength: startDose,
      frequency: "OD (morning)",
      instructions: "Recheck K+ in 4 wk. Hold if K ≥ 5.5. Continue ACEi/ARB + SGLT2i.",
    }),
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Levothyroxine (Hypothyroidism) --------
// ATA 2022:
//   Overt hypothyroidism (TSH > 10 OR TSH > 4.5 with symptoms / positive TPO):
//     Adults < 60 healthy: 1.6 µg/kg/day
//     Age ≥ 60 or cardiac: start 25-50 µg/day, up-titrate slowly.
//     Pregnancy: increase pre-pregnancy dose by 20-30 %.
export function suggestLevothyroxine(ctx) {
  const { age, weight, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const tsh = n(labs.tsh);
  const missing = [];
  if (tsh == null) missing.push("TSH");
  if (!weight) missing.push("weight");

  const hypothy = has(known, "hypothyroid", "subclinical hypothy") || /hypothyroid/i.test(dx);
  const eligible = hypothy || (tsh != null && tsh > 10);
  if (!eligible) {
    return { recommend: false, suggestions: [], missing, reason: "No hypothyroidism trigger (TSH > 10 or Hypothyroidism chip)." };
  }

  const already = alreadyOn(currentMeds, "levothyroxine", "thyronorm", "eltroxin", "l-thyroxine");
  const cautions = [];
  if (has(known, "CAD", "angina", "post MI")) cautions.push("Ischaemic heart disease — start 12.5-25 µg, titrate every 6-8 wk (rapid replacement can precipitate angina).");
  if (age != null && age >= 65) cautions.push("Age ≥ 65: start 25-50 µg/day, titrate q6-8 wk.");
  if (isPregnant(ctx)) cautions.push("Pregnancy: increase current dose by 20-30 % or start at 1.8 µg/kg — target TSH < 2.5 mIU/L in T1.");
  if (has(known, "adrenal insufficiency", "addison")) cautions.push("Untreated adrenal insufficiency — replace hydrocortisone FIRST; levothyroxine alone can precipitate crisis.");

  const w = weight || 70;
  let startMcg;
  if (has(known, "CAD", "angina", "post MI") || (age != null && age >= 65)) startMcg = 25;
  else startMcg = Math.round((1.6 * w) / 25) * 25; // round to nearest 25 µg
  if (startMcg < 25) startMcg = 25;

  const rationale = `Hypothyroidism (TSH ${tsh ?? "?"} mIU/L${weight ? `, weight ${weight} kg` : ""}). Starting dose based on weight & age (ATA 2022).`;
  const suggestions = [{
    regimen: "Levothyroxine — replacement",
    drug: "Levothyroxine (L-T4)",
    dose: `${startMcg} µg PO once daily on empty stomach (30-60 min before breakfast)`,
    titration: "Recheck TSH at 6-8 weeks. Adjust by 12.5-25 µg to keep TSH 0.5-2.5 (young) or 1-4 (elderly).",
    rationale,
    cautions: cautions.slice(),
    citations: ["ATA 2022 Hypothyroidism Guideline", "ETA 2013 Subclinical Hypothyroidism"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Thyronorm / Eltroxin / Thyrofit",
      generic_name: "Levothyroxine",
      composition: "Levothyroxine Sodium",
      strength: `${startMcg} µg`,
      frequency: "OD (empty stomach, 30-60 min pre-breakfast)",
      instructions: "Do not co-administer with calcium/iron/coffee/PPI (reduces absorption). Recheck TSH at 6-8 wk.",
    }),
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- DPP-4 inhibitors (Linagliptin / Sitagliptin / Vildagliptin) --------
// Weight-neutral, low-hypo. Linagliptin needs NO renal adjustment — the go-to
// in CKD. Saxagliptin has HF-hospitalisation signal (SAVOR-TIMI 53) — skip.
export function suggestDPP4(ctx) {
  const { age, egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const hba1c = n(labs.hba1c);
  const missing = [];
  if (hba1c == null) missing.push("HbA1c");

  const { t1, t2 } = ctxDiabetes(known, dx);
  if (!t2) return { recommend: false, suggestions: [], missing, reason: "Not indicated — T2DM only." };
  if (t1) return { recommend: false, suggestions: [], missing, reason: "T1DM — DPP-4i not indicated." };
  if (isPregnant(ctx)) {
    return {
      recommend: false, suggestions: [], missing,
      cautions: ["BLOCKED: Pregnancy / Gestational DM — no human safety data for DPP-4i; INSULIN is first-line."],
      reason: "Pregnancy / GDM — DPP-4i contraindicated; insulin is first-line.",
    };
  }

  const already = alreadyOn(currentMeds, "sitagliptin", "linagliptin", "vildagliptin", "teneligliptin", "saxagliptin", "alogliptin");
  const onGlp1 = alreadyOn(currentMeds, "semaglutide", "liraglutide", "dulaglutide", "tirzepatide");
  const cautions = [];
  if (has(known, "pancreatit")) cautions.push("History of pancreatitis — counsel on abdominal pain; discontinue if suspected.");
  if (onGlp1) cautions.push("BLOCKED: patient already on GLP-1 — do NOT combine DPP-4i (same pathway, no added benefit, more cost).");
  if (has(known, "heart failure")) cautions.push("HF: avoid Saxagliptin (SAVOR-TIMI ↑ HF hospitalisation). Linagliptin & Sitagliptin OK.");

  const blocked = onGlp1;
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Blocked — already on GLP-1." };

  const suggestions = [];
  // Linagliptin — no renal adjustment, first-line in CKD.
  suggestions.push({
    regimen: "Linagliptin — DPP-4i (CKD-friendly)",
    drug: "Linagliptin",
    dose: "5 mg PO once daily",
    titration: "Fixed dose — no titration. No renal or hepatic adjustment.",
    rationale: `T2DM add-on, HbA1c ${hba1c ?? "?"} %. Weight-neutral, low-hypo, no dose change in any CKD stage. Prefer as add-on to Metformin/SGLT2 when GLP-1 not affordable/available.`,
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9.2", "RSSDI 2024", "CARMELINA (2019) — CV & renal safety"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Trajenta / Lyxumia",
      generic_name: "Linagliptin",
      composition: "Linagliptin",
      strength: "5 mg",
      frequency: "OD",
      instructions: "Any time of day, with or without food. No dose adjustment for CKD/liver.",
    }),
  });
  // Sitagliptin — renal-adjusted.
  let sitaDose = "100 mg";
  let sitaGate = "eGFR ≥ 45";
  if (egfr != null && egfr < 30) { sitaDose = "25 mg"; sitaGate = "eGFR < 30"; }
  else if (egfr != null && egfr < 45) { sitaDose = "50 mg"; sitaGate = "eGFR 30-44"; }
  suggestions.push({
    regimen: "Sitagliptin — DPP-4i (renal-gated)",
    drug: "Sitagliptin",
    dose: `${sitaDose} PO once daily`,
    titration: `Fixed dose per eGFR (${sitaGate}). Recheck creatinine annually.`,
    rationale: "Alternative DPP-4i; cheaper than linagliptin but needs renal dose adjustment.",
    cautions: cautions.slice(),
    citations: ["TECOS (2015)", "ADA 2026 §9.2"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Januvia / Istavel / Zita",
      generic_name: "Sitagliptin",
      composition: "Sitagliptin",
      strength: sitaDose,
      frequency: "OD",
      instructions: `${sitaGate} — dose ${sitaDose}. Recheck creatinine annually.`,
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Sulfonylurea (Gliclazide MR / Glimepiride) --------
// Cheapest oral option. Prefer Gliclazide MR — lower hypo risk (ADVANCE) and
// no CV signal. Beers criteria (2023) recommends AVOIDING all SUs age ≥ 65 due
// to prolonged hypoglycaemia risk. Skip if patient on insulin already.
export function suggestSulfonylurea(ctx) {
  const { age, egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const hba1c = n(labs.hba1c);
  const missing = [];
  if (hba1c == null) missing.push("HbA1c");

  const { t1, t2 } = ctxDiabetes(known, dx);
  if (!t2 || t1) return { recommend: false, suggestions: [], missing, reason: "T2DM only." };
  if (isPregnant(ctx)) {
    return {
      recommend: false, suggestions: [], missing,
      cautions: ["BLOCKED: Pregnancy / Gestational DM — sulfonylureas cross the placenta (neonatal hypoglycaemia, macrosomia); INSULIN is first-line."],
      reason: "Pregnancy / GDM — sulfonylureas contraindicated; insulin is first-line.",
    };
  }

  const already = alreadyOn(currentMeds, "glimepiride", "gliclazide", "glipizide", "glibenclamide", "glyburide");
  const onInsulin = alreadyOn(currentMeds, "insulin", "glargine", "detemir", "degludec", "lispro", "aspart", "npH", "70/30");
  const cautions = [];
  if (age != null && age >= 65) cautions.push("Age ≥ 65: AGS Beers 2023 recommends AVOIDING SU due to prolonged hypoglycaemia — use SGLT2/GLP-1/DPP-4 instead.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75 / frail: strongly discouraged (hypo → falls/fractures).");
  if (egfr != null && egfr < 30) cautions.push("eGFR < 30: hypoglycaemia risk climbs — start at lowest dose, prefer Gliclazide over Glimepiride.");
  if (has(known, "recurrent hypoglyc", "hypo unawareness")) cautions.push("BLOCKED: recurrent hypoglycaemia / hypo unawareness.");
  if (has(known, "sulfa allergy")) cautions.push("BLOCKED: sulfa allergy.");
  if (onInsulin) cautions.push("Already on insulin — SU adds hypo risk with little glycaemic gain; consider deprescribing.");

  const blocked = has(known, "recurrent hypoglyc", "hypo unawareness", "sulfa allergy");
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Contraindicated — see cautions." };

  const suggestions = [];
  const startMr = age != null && age >= 65 ? "30 mg" : "30 mg"; // start low regardless
  suggestions.push({
    regimen: "Gliclazide MR — preferred SU (cost-tier)",
    drug: "Gliclazide (Modified Release)",
    dose: `${startMr} PO once daily with breakfast (titrate to max 120 mg OD)`,
    titration: "↑ 30 mg every 2-4 weeks based on FBS. Cap at 60 mg in eGFR 30-59.",
    rationale: `T2DM add-on when GLP-1/SGLT2/DPP-4 not affordable. Lower hypo than glimepiride (ADVANCE). HbA1c ${hba1c ?? "?"} %.`,
    cautions: cautions.slice(),
    citations: ["ADVANCE (2008)", "RSSDI 2024", "ADA 2026 §9.2"],
    recommended: !already && !(age != null && age >= 65),
    rx: mkOralRx({
      brand_name: "Diamicron MR / Reclide MR / Glizid MR",
      generic_name: "Gliclazide MR",
      composition: "Gliclazide (modified release)",
      strength: startMr,
      frequency: "OD (with breakfast)",
      instructions: "Take with breakfast. Titrate every 2-4 weeks. Cap at 60 mg if eGFR 30-59. Educate on hypo signs.",
    }),
  });
  suggestions.push({
    regimen: "Glimepiride — cheapest alternative",
    drug: "Glimepiride",
    dose: "1 mg PO once daily (max 4 mg OD)",
    titration: "↑ 1 mg every 1-2 wk, take with first meal. Skip dose if meal skipped.",
    rationale: "Cheapest SU. Higher hypo than gliclazide — reserve for cost-driven cases.",
    cautions: cautions.slice(),
    citations: ["ADA 2026 §9.2", "Beers Criteria 2023"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Amaryl / Glimestar / Glimy",
      generic_name: "Glimepiride",
      composition: "Glimepiride",
      strength: "1 mg",
      frequency: "OD (with breakfast)",
      instructions: "Skip if meal missed. Educate on hypoglycaemia signs.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Pioglitazone --------
// T2DM with MASLD/MASH or marked insulin resistance. AASLD 2023 endorses
// pioglitazone for biopsy-proven MASH. Avoid in HF, bladder cancer, osteoporosis.
export function suggestPioglitazone(ctx) {
  const { age, sex, egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const alt = n(labs.alt);
  const missing = [];

  const { t2 } = ctxDiabetes(known, dx);
  const masld = has(known, "MASLD", "NAFLD", "MASH", "fatty liver");
  const ir = has(known, "insulin resistance", "metabolic syndrome");
  if (!(t2 && (masld || ir))) {
    return { recommend: false, suggestions: [], missing, reason: "Requires T2DM + MASLD/MASH or overt insulin resistance." };
  }

  const already = alreadyOn(currentMeds, "pioglitazone", "rosiglitazone");
  const cautions = [];
  if (has(known, "heart failure")) cautions.push("BLOCKED: any HF — TZDs cause fluid retention.");
  if (has(known, "bladder ca", "bladder cancer")) cautions.push("BLOCKED: history/active bladder cancer — pioglitazone associated with recurrence.");
  if (has(known, "osteoporosis") && sex === "female") cautions.push("Post-menopausal osteoporosis: pioglitazone ↑ fracture risk — avoid or use lowest dose.");
  if (isPregnant(ctx)) cautions.push("BLOCKED: pregnancy.");
  if (alt != null && alt > 2.5 * 40) cautions.push("ALT > 2.5× ULN — investigate hepatic cause; monitor LFT every 3 months once started.");

  const blocked = has(known, "heart failure", "bladder ca", "bladder cancer") || isPregnant(ctx);
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication — see cautions." };

  const suggestions = [{
    regimen: "Pioglitazone — MASLD / insulin resistance",
    drug: "Pioglitazone",
    dose: "15 mg PO once daily → titrate to 30 mg (max 45 mg)",
    titration: "Start 15 mg OD × 4-6 wk. Up-titrate to 30 mg if HbA1c/ALT response inadequate. Recheck LFT + weight + oedema at 6-8 wk.",
    rationale: `T2DM with ${masld ? "MASLD/MASH" : "insulin resistance"}. AASLD 2023 endorses pioglitazone for biopsy-proven MASH (improves fibrosis).`,
    cautions: cautions.slice(),
    citations: ["AASLD 2023 MASLD", "PROactive (2005)", "IRIS trial (2016)", "ADA 2026 §9.2"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Pioglar / Piolim / Piozone",
      generic_name: "Pioglitazone",
      composition: "Pioglitazone",
      strength: "15 mg",
      frequency: "OD",
      instructions: "Recheck LFT + weight + oedema at 6-8 wk. Counsel on weight gain and pedal oedema.",
    }),
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Amlodipine (CCB — dihydropyridine) --------
// HTN first-line especially in isolated systolic HTN & elderly. Add-on to
// ACEi/ARB when BP not at goal. Useful in stable angina (Prinzmetal too).
export function suggestAmlodipine(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "" } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const sbp = n(ctx.vitals?.sbp) || n(ctx.sbp);
  const dbp = n(ctx.vitals?.dbp) || n(ctx.dbp);
  const missing = [];
  if (sbp == null && dbp == null) missing.push("BP");

  const htn = ctxHtn(known, dx, sbp, dbp);
  const angina = has(known, "angina", "stable angina", "prinzmetal");
  if (!htn && !angina) return { recommend: false, suggestions: [], missing, reason: "Requires HTN or angina indication." };

  const already = alreadyOn(currentMeds, "amlodipine", "nifedipine", "felodipine", "lercanidipine", "cilnidipine");
  const cautions = [];
  if (has(known, "severe aortic stenosis")) cautions.push("Severe aortic stenosis: use with caution — may reduce coronary perfusion.");
  if (has(known, "pedal oedema", "oedema")) cautions.push("Pre-existing oedema: expect worsening; consider adding ACEi/ARB (reduces amlodipine oedema).");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: start 2.5 mg, monitor postural BP + gum hyperplasia.");

  const trigger = htn && angina ? "HTN + Angina" : htn ? (sbp && sbp >= 160 ? "Stage 2 HTN" : "HTN") : "Angina";
  const rationale = `${trigger}. BP ${sbp ?? "?"}/${dbp ?? "?"}. Amlodipine is a preferred first-line CCB (long t½, once-daily, no reflex tachycardia). Excellent add-on to ACEi/ARB (ACCOMPLISH).`;
  const suggestions = [{
    regimen: "Amlodipine — CCB (HTN / angina)",
    drug: "Amlodipine",
    dose: `${age != null && age >= 75 ? "2.5" : "5"} mg PO once daily (max 10 mg)`,
    titration: "Recheck BP at 2-4 weeks. Double dose to 10 mg if BP > target. If pedal oedema — add ACEi/ARB (reduces oedema) or switch to Lercanidipine.",
    rationale,
    cautions: cautions.slice(),
    citations: ["ACCOMPLISH (2008)", "ASCOT-BPLA (2005)", "AHA/ACC HTN 2017", "JNC-8"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Amlong / Amlodac / Stamlo",
      generic_name: "Amlodipine",
      composition: "Amlodipine besylate",
      strength: age != null && age >= 75 ? "2.5 mg" : "5 mg",
      frequency: "OD (morning)",
      instructions: "Target BP < 130/80. Report ankle swelling. Recheck BP 2-4 wk.",
    }),
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Beta-blocker (Bisoprolol / Carvedilol / Metoprolol Succ) --------
// Four-pillar HFrEF, post-MI (≥ 3 yr benefit), AF rate control, stable angina.
// NOT routine first-line for uncomplicated HTN (per JNC-8) — only when
// coexisting angina/AF/HF/post-MI. Contraindications: asthma severe, heart
// block, decompensated HF, symptomatic bradycardia.
export function suggestBetaBlocker(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "" } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const hr = n(ctx.vitals?.pulse) || n(ctx.pulse);
  const missing = [];

  const hfref = has(known, "HFrEF", "heart failure (hfref)") || /hfref/i.test(dx);
  const postMi = has(known, "post MI", "myocardial infarction", "post-MI", "post pci", "cabg");
  const af = has(known, "atrial fibrillation", "AF", "atrial flutter");
  const angina = has(known, "angina", "stable angina", "CAD");
  const eligible = hfref || postMi || af || angina;
  if (!eligible) return { recommend: false, suggestions: [], missing, reason: "Requires HFrEF / Post-MI / AF / angina indication." };

  const already = alreadyOn(currentMeds, "bisoprolol", "carvedilol", "metoprolol", "nebivolol", "atenolol", "propranolol");
  const cautions = [];
  if (has(known, "asthma") && !hfref) cautions.push("Asthma: prefer cardioselective (Bisoprolol/Nebivolol). Avoid carvedilol/propranolol.");
  if (has(known, "asthma") && hfref) cautions.push("Severe asthma + HFrEF — cardiology-supervised low-dose Bisoprolol only.");
  if (has(known, "2° AV block", "3° AV block", "complete heart block")) cautions.push("BLOCKED: 2°/3° AV block without pacemaker.");
  if (hr != null && hr < 50) cautions.push(`BLOCKED: resting HR ${hr} bpm — symptomatic bradycardia.`);
  if (has(known, "decompensated hf", "acute pulmonary oedema")) cautions.push("Decompensated HF: stabilise volume first; start BB only when euvolaemic.");
  if (has(known, "peripheral arter")) cautions.push("PAD: prefer cardioselective (Bisoprolol); avoid non-selective.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: start at half the lowest tablet strength; titrate q2-4 weeks.");

  const blocked = has(known, "2° AV block", "3° AV block", "complete heart block") || (hr != null && hr < 50);
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but contraindication — see cautions." };

  const suggestions = [];
  const trigger = hfref ? "HFrEF (four-pillar)" : postMi ? "Post-MI (mortality benefit)" : af ? "AF rate control" : "Stable angina";
  // Bisoprolol first — best evidence in HFrEF (CIBIS-II), cardioselective.
  suggestions.push({
    regimen: "Bisoprolol — cardioselective β-blocker",
    drug: "Bisoprolol",
    dose: `${hfref ? "1.25" : "2.5"} mg PO once daily → titrate to 10 mg (target)`,
    titration: hfref ? "HFrEF: 1.25 → 2.5 → 3.75 → 5 → 7.5 → 10 mg, doubling every 2 weeks if HR/BP tolerate. Never up-titrate if HR < 55 or SBP < 100." : "Non-HF: 2.5 → 5 → 10 mg every 2-4 weeks. Cap at 10 mg.",
    rationale: `${trigger}. Bisoprolol has the strongest mortality data in HFrEF (CIBIS-II) and is cardioselective (safer in COPD/PAD).`,
    cautions: cautions.slice(),
    citations: ["CIBIS-II (1999)", "ESC HF 2023", "AHA/ACC 2023 CCD", "ACC/AHA HF 2022"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Concor / Corbis / Bisoheart",
      generic_name: "Bisoprolol",
      composition: "Bisoprolol fumarate",
      strength: hfref ? "1.25 mg" : "2.5 mg",
      frequency: "OD (morning)",
      instructions: `Target 10 mg OD. Titrate every 2-4 wk. Hold if HR < 55 or SBP < 100. Report fatigue / cold extremities.`,
    }),
  });
  // Carvedilol — non-selective + α-block, HFrEF benefit (COPERNICUS).
  if (hfref || postMi) {
    suggestions.push({
      regimen: "Carvedilol — α+β-blocker (HFrEF)",
      drug: "Carvedilol",
      dose: "3.125 mg PO BID → titrate to 25 mg BID (or 50 mg BID if weight > 85 kg)",
      titration: "Double dose every 2 weeks with meals. Watch orthostasis.",
      rationale: "HFrEF mortality benefit (COPERNICUS, CAPRICORN). α-blockade also lowers BP — useful when SBP high.",
      cautions: cautions.slice(),
      citations: ["COPERNICUS (2001)", "CAPRICORN (2001)", "ESC HF 2023"],
      recommended: false,
      rx: mkOralRx({
        brand_name: "Carca / Carvistar / Cardivas",
        generic_name: "Carvedilol",
        composition: "Carvedilol",
        strength: "3.125 mg",
        frequency: "BD (with meals)",
        instructions: "Take with meals to slow absorption (↓ orthostasis). Titrate q2 weeks.",
      }),
    });
  }
  // Metoprolol succinate — once-daily HF option (MERIT-HF).
  if (hfref || af || postMi) {
    suggestions.push({
      regimen: "Metoprolol succinate — once-daily β1",
      drug: "Metoprolol Succinate (Extended Release)",
      dose: "25 mg PO OD → titrate to 200 mg OD (target)",
      titration: "Double every 2 weeks. Prefer succinate (24-hr cover) over tartrate (BID, less HF data).",
      rationale: "Cardioselective, once-daily. MERIT-HF showed 34 % mortality reduction in HFrEF.",
      cautions: cautions.slice(),
      citations: ["MERIT-HF (1999)"],
      recommended: false,
      rx: mkOralRx({
        brand_name: "Metpure XL / Seloken XL / Betaloc",
        generic_name: "Metoprolol Succinate",
        composition: "Metoprolol Succinate (extended release)",
        strength: "25 mg",
        frequency: "OD",
        instructions: "Swallow whole (do not crush). Titrate every 2 weeks to target 200 mg.",
      }),
    });
  }

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Loop diuretic (Furosemide / Torsemide) --------
// HFrEF/HFpEF with congestion, CKD volume overload, resistant HTN.
export function suggestLoopDiuretic(ctx) {
  const { egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const k = n(labs.potassium) || n(labs.k);
  const na = n(labs.sodium);
  const missing = [];
  if (k == null) missing.push("Serum potassium");

  const hf = ctxHeartFailure(known, dx);
  const congestion = has(known, "pedal oedema", "pulmonary oedema", "ascites", "anasarca", "volume overload");
  const ckdOverload = (egfr != null && egfr < 30) && has(known, "oedema", "volume overload");
  const eligible = hf || congestion || ckdOverload;
  if (!eligible) return { recommend: false, suggestions: [], missing, reason: "Requires HF / congestion / CKD volume overload." };

  const already = alreadyOn(currentMeds, "furosemide", "torsemide", "torasemide", "bumetanide");
  const cautions = [];
  if (k != null && k < 3.5) cautions.push("Hypokalaemia (K < 3.5): replace K/switch/add MRA before adding loop.");
  if (na != null && na < 130) cautions.push("Hyponatraemia (Na < 130): titrate cautiously; hold if Na drops further.");
  if (has(known, "gout")) cautions.push("Gout: loops raise urate — consider allopurinol cover.");
  if (has(known, "anuria")) cautions.push("BLOCKED: anuria.");

  if (has(known, "anuria")) return { recommend: true, suggestions: [], missing, cautions, reason: "Contraindicated in anuria." };

  const suggestions = [];
  suggestions.push({
    regimen: "Furosemide — first-line loop",
    drug: "Furosemide",
    dose: "20-40 mg PO OD-BID (up-titrate to symptom relief; max 240 mg/day)",
    titration: "Weigh daily; target dry weight. Double the dose (not the frequency) until diuresis. Recheck K, Na, creat at 3-7 days.",
    rationale: `${hf ? "HFrEF/HFpEF congestion" : "Volume overload"}. Cheapest loop, immediate-release, 4-6 hr action.`,
    cautions: cautions.slice(),
    citations: ["ESC HF 2023", "AHA/ACC HF 2022"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Lasix / Frusenex",
      generic_name: "Furosemide",
      composition: "Furosemide",
      strength: "40 mg",
      frequency: "OD (morning)",
      instructions: "Take in morning to avoid nocturia. Daily weight log. Recheck K/Na/Creat at 3-7 days.",
    }),
  });
  suggestions.push({
    regimen: "Torsemide — better absorption",
    drug: "Torsemide",
    dose: "10-20 mg PO OD (up-titrate to 40 mg; 1 mg torsemide ≈ 2 mg furosemide)",
    titration: "More predictable absorption than furosemide (esp in HF gut oedema). TRANSFORM-HF (2023) — no mortality difference, but symptom control often better.",
    rationale: "Higher and more consistent oral bioavailability (~80-100 %) — preferred when furosemide response is erratic.",
    cautions: cautions.slice(),
    citations: ["TRANSFORM-HF (2023)", "ESC HF 2023"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Dytor / Torsid / Diucontin",
      generic_name: "Torsemide",
      composition: "Torsemide",
      strength: "10 mg",
      frequency: "OD (morning)",
      instructions: "Daily weight log. Recheck K/Na/Creat at 3-7 days. Better absorption than furosemide.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Thiazide-like diuretic (Chlorthalidone / Indapamide) --------
// HTN first-line combo with ACEi/ARB. Chlorthalidone > HCTZ (longer t½,
// stronger evidence — ALLHAT, SPRINT). Loses efficacy below eGFR 30.
export function suggestThiazide(ctx) {
  const { age, egfr, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const sbp = n(ctx.vitals?.sbp) || n(ctx.sbp);
  const dbp = n(ctx.vitals?.dbp) || n(ctx.dbp);
  const k = n(labs.potassium) || n(labs.k);
  const na = n(labs.sodium);
  const missing = [];
  if (k == null) missing.push("Serum potassium");

  const htn = ctxHtn(known, dx, sbp, dbp);
  if (!htn) return { recommend: false, suggestions: [], missing, reason: "Requires HTN." };

  const already = alreadyOn(currentMeds, "chlorthalidone", "chlortalidone", "indapamide", "hydrochlorothiazide", "hctz", "metolazone");
  const cautions = [];
  if (egfr != null && egfr < 30) cautions.push("BLOCKED: eGFR < 30 — thiazide loses efficacy; switch to loop diuretic.");
  if (k != null && k < 3.5) cautions.push("Hypokalaemia (K < 3.5): correct before starting; consider K-sparing combo (Losartan/ACEi/MRA).");
  if (has(known, "gout")) cautions.push("Gout: thiazides raise urate — avoid or cover with allopurinol.");
  if (has(known, "sulfa allergy")) cautions.push("Sulfa allergy: rare cross-reactivity — cautious use.");
  if (na != null && na < 132) cautions.push("Hyponatraemia (Na < 132): thiazide-induced hypoNa; avoid.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: start 6.25 mg chlorthalidone (or 1.25 mg indapamide) — hyponatraemia risk.");

  const blocked = (egfr != null && egfr < 30);
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "eGFR < 30 — thiazide ineffective. Use loop." };

  const suggestions = [];
  suggestions.push({
    regimen: "Chlorthalidone — preferred thiazide-like (SPRINT/ALLHAT)",
    drug: "Chlorthalidone",
    dose: `${age != null && age >= 75 ? "6.25" : "12.5"} mg PO once daily (max 25 mg)`,
    titration: "Recheck BP, K+, Na+ at 2-4 weeks. Up-titrate to 25 mg if BP > target. Combine with ACEi/ARB or CCB for optimal effect.",
    rationale: `HTN. BP ${sbp ?? "?"}/${dbp ?? "?"}. Chlorthalidone has the strongest outcomes data (ALLHAT, SPRINT) — longer t½ (~50 h) means smoother 24-hr BP control than HCTZ.`,
    cautions: cautions.slice(),
    citations: ["ALLHAT (2002)", "SPRINT (2015)", "AHA/ACC HTN 2017", "JNC-8"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Hythalton / Thalitone",
      generic_name: "Chlorthalidone",
      composition: "Chlorthalidone",
      strength: age != null && age >= 75 ? "6.25 mg" : "12.5 mg",
      frequency: "OD (morning)",
      instructions: "Target BP < 130/80. Recheck K/Na at 2-4 wk. Report cramps / weakness.",
    }),
  });
  suggestions.push({
    regimen: "Indapamide SR — alternative thiazide-like",
    drug: "Indapamide SR",
    dose: "1.5 mg PO once daily (fixed dose)",
    titration: "Fixed dose. Recheck BP + electrolytes at 4 weeks. Slightly milder metabolic effect than chlorthalidone.",
    rationale: "HYVET data — good in the very elderly (age ≥ 80). Fewer glucose/lipid effects than HCTZ.",
    cautions: cautions.slice(),
    citations: ["HYVET (2008)", "ESC HTN 2018"],
    recommended: false,
    rx: mkOralRx({
      brand_name: "Natrilix SR / Lorvas SR",
      generic_name: "Indapamide SR",
      composition: "Indapamide (sustained release)",
      strength: "1.5 mg",
      frequency: "OD (morning)",
      instructions: "Fixed dose. Recheck K+/Na+ at 4 wk.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Ezetimibe --------
// Add-on when max-tolerated statin doesn't hit LDL goal, or statin-intolerant.
export function suggestEzetimibe(ctx) {
  const { known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const ldl = n(labs.ldl);
  const missing = [];
  if (ldl == null) missing.push("LDL");

  const ascvd = ctxAscvd(known, dx);
  const { t2 } = ctxDiabetes(known, dx);
  const statinIntol = has(known, "statin intol", "statin myopathy", "rhabdomyolysis");
  const onStatin = alreadyOn(currentMeds, "rosuvastatin", "atorvastatin", "simvastatin", "pravastatin", "pitavastatin");
  const ldlGoalMissed = (ascvd && ldl != null && ldl >= 55) || (t2 && ldl != null && ldl >= 70) || (ldl != null && ldl >= 100);
  const eligible = statinIntol || (onStatin && ldlGoalMissed) || (ldl != null && ldl >= 190);
  if (!eligible) return { recommend: false, suggestions: [], missing, reason: "Add when max-tolerated statin doesn't hit LDL goal, or statin-intolerant." };

  const already = alreadyOn(currentMeds, "ezetimibe");
  const cautions = [];
  if (isPregnant(ctx)) cautions.push("BLOCKED: pregnancy (limited safety data with statin combo).");
  if (has(known, "cirrhosis", "decompensated liver")) cautions.push("Avoid in active hepatic disease (esp with statin).");

  const blocked = isPregnant(ctx);
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Contraindicated in pregnancy." };

  const rationale = statinIntol
    ? `Statin intolerance — ezetimibe monotherapy lowers LDL ~18 %.`
    : `Statin add-on: LDL ${ldl ?? "?"} above goal. IMPROVE-IT: statin+ezetimibe ↓ MACE 6.4 % vs statin alone. RACING: combo ≥ dose-doubling with fewer side-effects.`;
  const suggestions = [{
    regimen: statinIntol ? "Ezetimibe — statin-intolerant monotherapy" : "Ezetimibe — statin add-on",
    drug: "Ezetimibe",
    dose: "10 mg PO once daily (any time)",
    titration: "Fixed dose. Recheck lipids at 6-8 weeks. If still not at goal, add bempedoic acid or PCSK9 inhibitor.",
    rationale,
    cautions: cautions.slice(),
    citations: ["IMPROVE-IT (2015)", "RACING (2022)", "AHA/ACC 2023 CCD", "AACE 2024"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Ezetib / Ezedoc / Zetia",
      generic_name: "Ezetimibe",
      composition: "Ezetimibe",
      strength: "10 mg",
      frequency: "OD (any time)",
      instructions: "Take with or without food, any time. Recheck lipids at 6-8 wk.",
    }),
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Vitamin D3 (Cholecalciferol) --------
// Endocrine Society 2011: Deficient < 20 ng/mL; Insufficient 20-30. Treat
// deficiency with 60,000 IU/wk × 8 wk then 60,000 IU/mo maintenance.
export function suggestVitaminD(ctx) {
  const { known = [], currentMeds = [], labs = {} } = ctx;
  const vitD = n(labs.vit_d);
  const missing = [];
  if (vitD == null && !has(known, "vitamin d defic", "osteoporosis")) missing.push("25-OH Vitamin D level");

  const deficient = (vitD != null && vitD < 20) || has(known, "vitamin d defic");
  const insufficient = (vitD != null && vitD >= 20 && vitD < 30);
  const osteoporosis = has(known, "osteoporosis", "osteopenia");
  const eligible = deficient || (insufficient && osteoporosis) || (osteoporosis && vitD == null);
  if (!eligible) return { recommend: false, suggestions: [], missing, reason: "Requires 25-OH Vit D < 20 ng/mL, or insufficient (20-30) with osteoporosis, or osteoporosis chip." };

  const already = alreadyOn(currentMeds, "cholecalciferol", "vitamin d", "calcitriol", "vit d", "d3");
  const cautions = [];
  if (has(known, "sarcoid", "granulomatous", "primary hyperparathyroid", "hypercalcaemia")) cautions.push("BLOCKED: sarcoidosis / granulomatous disease / hypercalcaemia — 1,25-OH-D upregulation risk.");
  if (has(known, "renal calculi", "nephrolithiasis")) cautions.push("History of kidney stones — check calcium & urinary calcium before/during therapy.");

  const blocked = has(known, "sarcoid", "granulomatous", "primary hyperparathyroid", "hypercalcaemia");
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Contraindication — see cautions." };

  const suggestions = [];
  if (deficient) {
    suggestions.push({
      regimen: "Vitamin D3 — loading (deficient)",
      drug: "Cholecalciferol (D3) — high-dose loading",
      dose: "60,000 IU PO weekly × 8 weeks → then 60,000 IU monthly (maintenance)",
      titration: "Recheck 25-OH Vit D after 8-12 weeks. Target 30-60 ng/mL. Continue monthly maintenance thereafter.",
      rationale: `25-OH-D ${vitD ?? "?"} ng/mL → deficient. Endocrine Society: loading dose then maintenance.`,
      cautions: cautions.slice(),
      citations: ["Endocrine Society 2011 (Holick)", "IOF 2010"],
      recommended: !already,
      rx: mkOralRx({
        brand_name: "Uprise D3 / D-Rise / Calcirol",
        generic_name: "Cholecalciferol (D3)",
        composition: "Cholecalciferol 60,000 IU",
        strength: "60,000 IU",
        frequency: "Once weekly × 8 wk, then monthly",
        instructions: "Take with fatty meal. Recheck 25-OH Vit D after 8-12 wk. Also ensure calcium 1000-1200 mg/day (diet).",
      }),
    });
  } else {
    suggestions.push({
      regimen: "Vitamin D3 — maintenance (insufficient / osteoporosis)",
      drug: "Cholecalciferol (D3) — maintenance",
      dose: "60,000 IU PO once monthly (or 1000-2000 IU daily)",
      titration: "Recheck at 3 months. Add calcium 1000-1200 mg/day for osteoporosis.",
      rationale: `25-OH-D ${vitD ?? "?"} ng/mL — insufficient. Long-term maintenance for bone/muscle health.`,
      cautions: cautions.slice(),
      citations: ["Endocrine Society 2011", "IOF 2010"],
      recommended: !already,
      rx: mkOralRx({
        brand_name: "Uprise D3 / D-Rise / Calcirol",
        generic_name: "Cholecalciferol (D3)",
        composition: "Cholecalciferol 60,000 IU",
        strength: "60,000 IU",
        frequency: "Once monthly",
        instructions: "With fatty meal. Ensure dietary calcium 1000-1200 mg/day.",
      }),
    });
  }

  return { recommend: true, suggestions, missing, cautions };
}

// -------- Pantoprazole (PPI) — GI prophylaxis / PUD / GERD --------
// Trigger when patient is on aspirin/NSAID plus risk factor (age ≥ 65,
// PUD history, dual antiplatelet, oral anticoagulant) OR active GERD/PUD.
export function suggestPPI(ctx) {
  const { age, known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "" } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const missing = [];

  const onAspirin = alreadyOn(currentMeds, "aspirin", "acetylsalicylic", "ecosprin");
  const onNSAID = alreadyOn(currentMeds, "ibuprofen", "diclofenac", "naproxen", "etoricoxib", "aceclofenac", "indomethacin");
  const onDapt = alreadyOn(currentMeds, "clopidogrel", "prasugrel", "ticagrelor");
  const onDoac = alreadyOn(currentMeds, "warfarin", "apixaban", "rivaroxaban", "dabigatran", "edoxaban");
  const gerd = has(known, "GERD", "reflux", "peptic ulcer", "PUD", "gastritis", "erosive esophagitis") || /gerd|reflux|peptic|pud/i.test(dx);
  const priorPud = has(known, "peptic ulcer", "PUD", "GI bleed", "GI bleeding");
  const highRiskAspirin = (onAspirin || onNSAID) && (
    (age != null && age >= 65) || priorPud || onDapt || onDoac || has(known, "steroid")
  );

  const eligible = gerd || highRiskAspirin || priorPud;
  if (!eligible) return { recommend: false, suggestions: [], missing, reason: "Requires GERD / PUD / high-risk aspirin or NSAID use." };

  const already = alreadyOn(currentMeds, "pantoprazole", "omeprazole", "rabeprazole", "esomeprazole", "lansoprazole", "dexrabeprazole");
  const cautions = [];
  if (has(known, "osteoporosis")) cautions.push("Osteoporosis: long-term PPI ↑ fracture risk — plan deprescription review annually.");
  if (has(known, "chronic kidney") || has(known, "CKD")) cautions.push("Chronic PPI use associated with AIN & progression of CKD — reassess need at each visit.");
  if (has(known, "b12 deficiency", "hypomagnesaemia")) cautions.push("Long-term PPI can worsen B12 / Mg deficiency — monitor annually.");
  if (age != null && age >= 75) cautions.push("Age ≥ 75: use lowest effective dose; deprescribe when possible (Beers criteria).");

  const suggestions = [];
  const gerdDur = gerd ? "8 weeks (GERD/PUD); step down / discontinue thereafter" : "Ongoing — reassess annually";
  suggestions.push({
    regimen: gerd ? "Pantoprazole — PUD / GERD" : "Pantoprazole — GI prophylaxis (aspirin/NSAID)",
    drug: "Pantoprazole",
    dose: "40 mg PO once daily 30 min before breakfast",
    titration: "Reassess symptoms in 4-8 weeks. Step down to 20 mg or on-demand once symptoms controlled.",
    rationale: gerd
      ? `${dx || "GERD / PUD"} — PPI is first-line acid suppression.`
      : `On ${onAspirin ? "aspirin" : "NSAID"}${onDapt ? " + DAPT" : ""}${onDoac ? " + anticoagulant" : ""}${priorPud ? " with prior PUD" : ""}${age != null && age >= 65 ? ", age ≥ 65" : ""} — high GI bleed risk warrants PPI cover.`,
    cautions: cautions.slice(),
    citations: ["ACG 2022 GERD & PUD", "COGENT (2010) — PPI safety with clopidogrel", "ACC/AHA 2023"],
    recommended: !already,
    rx: mkOralRx({
      brand_name: "Pan / Pantocid / Pantop",
      generic_name: "Pantoprazole",
      composition: "Pantoprazole",
      strength: "40 mg",
      frequency: "OD (30 min pre-breakfast)",
      duration: gerdDur,
      instructions: "Take 30-60 min before first meal. Deprescribe when possible if long-term.",
    }),
  });

  return { recommend: true, suggestions, missing, cautions };
}

// -------- ARNI (Sacubitril + Valsartan) — HFrEF cornerstone --------
// PARADIGM-HF (2014): ARNI beat enalapril by 20 % on CV death / HF admission.
// ESC HF 2023 & AHA/ACC 2022 both list ARNI as PREFERRED RAAS agent in HFrEF
// (Class I). Blockers: pregnancy, K > 5.5, eGFR < 30, angioedema history,
// current ACEi (must have 36-h washout).
export function suggestARNI(ctx) {
  const { known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const sbp = n(ctx.vitals?.sbp) || n(ctx.sbp);
  const egfr = n(ctx.egfr);
  const k = n(labs.potassium) || n(labs.k);
  const missing = [];
  if (k == null) missing.push("Serum potassium");
  if (sbp == null) missing.push("SBP");
  if (egfr == null) missing.push("eGFR");

  const hfref = has(known, "HFrEF", "heart failure (hfref)") || /hfref/i.test(dx);
  if (!hfref) return { recommend: false, suggestions: [], missing, reason: "HFrEF (LVEF ≤ 40 %) is required." };

  const already = alreadyOn(currentMeds, "sacubitril", "arni", "vymada", "entresto", "cidmus");
  const onACEi = alreadyOn(currentMeds, "ramipril", "enalapril", "lisinopril", "perindopril", "captopril", "benazepril");
  const cautions = [];
  if (isPregnant(ctx)) cautions.push("BLOCKED: pregnancy — ARNI teratogenic (contains valsartan).");
  if (k != null && k > 5.5) cautions.push("BLOCKED: hyperkalaemia (K > 5.5) — correct first.");
  if (egfr != null && egfr < 30) cautions.push("BLOCKED: eGFR < 30 — start ACEi/ARB first; ARNI trials excluded severe CKD.");
  if (sbp != null && sbp < 100) cautions.push("BLOCKED: SBP < 100 mmHg — hypotension risk. Optimise volume first.");
  if (has(known, "angioedema")) cautions.push("BLOCKED: history of angioedema — do NOT use.");
  if (onACEi) cautions.push("Currently on ACEi — WASHOUT ≥ 36 hours BEFORE starting ARNI (angioedema risk). Deprescribe ACEi first.");

  const blocked = isPregnant(ctx) || (k != null && k > 5.5) || (egfr != null && egfr < 30) || (sbp != null && sbp < 100) || has(known, "angioedema");
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "HFrEF confirmed but hard contraindication — see cautions." };

  // Starting dose — reduced if severe HF or first-time RAASi user.
  const naive = !alreadyOn(currentMeds, "ramipril", "enalapril", "lisinopril", "perindopril", "captopril", "telmisartan", "losartan", "olmesartan", "valsartan", "irbesartan", "candesartan");
  const startStrength = naive || (sbp != null && sbp < 120) || (egfr != null && egfr < 60) ? "24/26 mg" : "49/51 mg";
  const rationale = `HFrEF confirmed. SBP ${sbp ?? "?"}, eGFR ${egfr ?? "?"}, K ${k ?? "?"}. ARNI reduced CV death / HF admission by 20 % vs enalapril (PARADIGM-HF) — Class I recommendation over ACEi/ARB in HFrEF.`;
  const suggestions = [{
    regimen: "Sacubitril + Valsartan (ARNI) — HFrEF cornerstone",
    drug: "Sacubitril/Valsartan",
    dose: `${startStrength} PO BD → titrate to 97/103 mg BD target every 2-4 weeks`,
    titration: onACEi
      ? `⚠ 36-hr washout of ACEi FIRST. Start ${startStrength} BD. Recheck BP + K + creat + eGFR in 1-2 wk. Double dose every 2-4 wk to 49/51 → 97/103 mg BD.`
      : `Start ${startStrength} BD. Recheck BP + K + creat + eGFR in 1-2 wk. Double dose every 2-4 wk to target 97/103 mg BD.`,
    rationale,
    cautions: cautions.slice(),
    citations: ["PARADIGM-HF (2014)", "ESC HF 2023 §7 (Class I)", "AHA/ACC 2022 HF (Stage C)", "PIONEER-HF (2019 — in-hospital initiation)"],
    recommended: !already && !blocked,
    rx: {
      brand_name: "Vymada / Cidmus / Entresto",
      generic_name: "Sacubitril + Valsartan",
      composition: "Sacubitril + Valsartan",
      strength: startStrength,
      route: "Oral",
      dosage_form: "Tablet",
      frequency: "BD (morning & evening)",
      duration: "Ongoing — HFrEF cornerstone; do not stop without cardiology input",
      instructions: onACEi
        ? "⚠ 36-hr washout of current ACEi before first dose. Recheck BP + K + creat at 1-2 wk. Titrate to 97/103 mg BD."
        : "Recheck BP + K + creat + eGFR at 1-2 wk. Titrate every 2-4 wk. Hold if SBP < 95 or K > 5.5.",
      notes: "",
      quantity: "",
      type: "chronic",
    },
  }];

  return { recommend: true, suggestions, missing, cautions };
}

// -------- MRA for HFrEF (Spironolactone / Eplerenone) --------
// ESC HF 2023 Class I in HFrEF (LVEF ≤ 40 %). Distinct from Finerenone
// (Finerenone reserved for DKD, not HFrEF).
export function suggestMraHF(ctx) {
  const { known = [], currentMeds = [], diagnosisPrimary = "", diagnosisSecondary = "", labs = {} } = ctx;
  const dx = [diagnosisPrimary, diagnosisSecondary].filter(Boolean).join(" · ");
  const egfr = n(ctx.egfr);
  const k = n(labs.potassium) || n(labs.k);
  const missing = [];
  if (k == null) missing.push("Serum potassium");
  if (egfr == null) missing.push("eGFR");

  const hfref = has(known, "HFrEF", "heart failure (hfref)") || /hfref/i.test(dx);
  const postMi = has(known, "post MI", "myocardial infarction") && has(known, "lv dysfunction");
  if (!hfref && !postMi) return { recommend: false, suggestions: [], missing, reason: "Requires HFrEF (LVEF ≤ 40 %) or post-MI LV dysfunction." };

  const already = alreadyOn(currentMeds, "spironolactone", "eplerenone");
  const cautions = [];
  if (k != null && k > 5.0) cautions.push("BLOCKED: K > 5.0 — correct first; MRA is contraindicated.");
  if (egfr != null && egfr < 30) cautions.push("BLOCKED: eGFR < 30 — hyperkalaemia risk too high.");
  if (isPregnant(ctx)) cautions.push("BLOCKED: pregnancy (spironolactone is anti-androgenic).");
  if (alreadyOn(currentMeds, "finerenone")) cautions.push("BLOCKED: already on Finerenone (non-steroidal MRA) — do not combine.");
  if (has(known, "gynecomastia") && !already) cautions.push("Gynaecomastia risk with spironolactone — prefer Eplerenone (α-selective).");

  const blocked = (k != null && k > 5.0) || (egfr != null && egfr < 30) || isPregnant(ctx) || alreadyOn(currentMeds, "finerenone");
  if (blocked) return { recommend: true, suggestions: [], missing, cautions, reason: "Eligible but hard contraindication — see cautions." };

  const suggestions = [];
  suggestions.push({
    regimen: "Spironolactone — first-line MRA in HFrEF",
    drug: "Spironolactone",
    dose: "25 mg PO once daily → titrate to 50 mg OD (target)",
    titration: "Recheck K+ + creat at 1, 4 & 12 wk. Halve dose or stop if K > 5.5.",
    rationale: `HFrEF — RALES showed 30 % mortality reduction. Cheap, once-daily, robust evidence.`,
    cautions: cautions.slice(),
    citations: ["RALES (1999)", "ESC HF 2023 §7 (Class I)", "AHA/ACC 2022 HF"],
    recommended: !already,
    rx: {
      brand_name: "Aldactone / Spirono",
      generic_name: "Spironolactone",
      composition: "Spironolactone",
      strength: "25 mg",
      route: "Oral",
      dosage_form: "Tablet",
      frequency: "OD (morning)",
      duration: "Ongoing — HFrEF pillar",
      instructions: "Recheck K+ + creat at 1, 4 & 12 wk. Report breast tenderness (switch to eplerenone).",
      notes: "", quantity: "", type: "chronic",
    },
  });
  suggestions.push({
    regimen: "Eplerenone — selective MRA (no gynaecomastia)",
    drug: "Eplerenone",
    dose: "25 mg PO once daily → titrate to 50 mg OD",
    titration: "Recheck K+ + creat at 1 & 4 wk. Prefer over spironolactone in men with gynaecomastia or post-MI LV dysfunction.",
    rationale: "EPHESUS (post-MI LV dysfunction) + EMPHASIS-HF (NYHA II HFrEF) — mortality benefit. Selective, avoids anti-androgen effect.",
    cautions: cautions.slice(),
    citations: ["EPHESUS (2003)", "EMPHASIS-HF (2011)"],
    recommended: false,
    rx: {
      brand_name: "Inspra / Eplenerone",
      generic_name: "Eplerenone",
      composition: "Eplerenone",
      strength: "25 mg",
      route: "Oral",
      dosage_form: "Tablet",
      frequency: "OD (morning)",
      duration: "Ongoing",
      instructions: "Recheck K+ + creat at 1 & 4 wk. Costlier than spironolactone but no gynaecomastia.",
      notes: "", quantity: "", type: "chronic",
    },
  });

  return { recommend: true, suggestions, missing, cautions };
}

// Combined convenience helper — returns every push engine's output keyed by class.
// Order in returned object is UI display order (top → bottom in the panel).
export function suggestAllPushDrugs(ctx) {
  // ARNI takes precedence over ACEi/ARB in HFrEF — if ARNI is recommended,
  // demote the ACEi/ARB suggestion so the doctor sees ARNI as the "first-line".
  const arni = suggestARNI(ctx);
  const aceiarb = suggestACEiARB(ctx);
  if (arni.recommend && arni.suggestions?.length && aceiarb.recommend && aceiarb.suggestions?.length) {
    aceiarb.suggestions = aceiarb.suggestions.map((s) => ({
      ...s,
      recommended: false,
      cautions: [
        "⚠ HFrEF present — ARNI (Sacubitril/Valsartan) is PREFERRED (ESC HF 2023 Class I). Use this only if ARNI not affordable / available.",
        ...(s.cautions || []),
      ],
    }));
    aceiarb.reason = "HFrEF present — ARNI is first-line; ACEi/ARB shown as fallback.";
  }
  return {
    metformin: suggestMetformin(ctx),
    sglt2: suggestSGLT2(ctx),
    dpp4: suggestDPP4(ctx),
    sulfonylurea: suggestSulfonylurea(ctx),
    pioglitazone: suggestPioglitazone(ctx),
    statin: suggestStatin(ctx),
    ezetimibe: suggestEzetimibe(ctx),
    arni,
    aceiarb,
    amlodipine: suggestAmlodipine(ctx),
    thiazide: suggestThiazide(ctx),
    beta_blocker: suggestBetaBlocker(ctx),
    mra_hf: suggestMraHF(ctx),
    loop_diuretic: suggestLoopDiuretic(ctx),
    aspirin: suggestAspirin(ctx),
    finerenone: suggestFinerenone(ctx),
    ppi: suggestPPI(ctx),
    vitamin_d: suggestVitaminD(ctx),
    levothyroxine: suggestLevothyroxine(ctx),
  };
}

// Convenience combined helper.
export function suggestInjectables(ctx) {
  return { insulin: suggestInsulin(ctx), glp1: suggestGLP1(ctx) };
}
